import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  TextField,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { TopBar, LeadCard } from "./telecalling";
import CallOutcomeForm from "./CallOutcomeForm";
import { isLoggedIn, getUserId } from "../../../Utility/helper";
import "./telecalling.css";

export default function TelecallingWorkingPage() {
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [setHistoryOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [dataType, setDataType] = useState("general");
  const [searchTeleId, setSearchTeleId] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    severity: "success",
    message: "",
  });

  useEffect(() => {
    if (!isLoggedIn()) {
      console.warn("🔒 User not logged in. Redirecting to login.");
      navigate("/login");
    }
  }, [navigate]);

  const fetchLead = useCallback(async () => {
    setLoading(true);
    setError("");
    setLead(null);

    try {
      const userId = getUserId();
      console.log("📡 Fetching lead. DataType:", dataType, "UserID:", userId);

      let url = "https://be.corebankers.in/telecalling";

      if (dataType === "eligibility") {
        if (!userId) {
          console.warn("⚠️ Missing userId for eligibility fetch");
          setError("Session expired or user ID missing");
          setLoading(false);
          return;
        }
        url = `https://be.corebankers.in/telecalling-eligibility?admin_id=${userId}`;
      } else if (dataType === "callback") {
        console.log("📞 Callback queue selected");
        setLead({
          name: "Callback Queue",
          mobile_number: "—",
          loan_type: "",
          disposition: "Pending Callback",
          callStatus: "—",
          history: [],
        });
        setLoading(false);
        return;
      } else if (dataType === "search") {
        if (!searchTeleId.trim()) {
          console.warn("🔍 Empty Tele ID search");
          setError("Please enter a Tele ID to search");
          setLoading(false);
          return;
        }
        url = `https://be.corebankers.in/telecalling-by-id?tele_id=${searchTeleId.trim()}`;
      } else {
        if (!userId) {
          console.warn("⚠️ Missing userId for general fetch");
          setError("Session expired or user ID missing");
          setLoading(false);
          return;
        }
        url = `https://be.corebankers.in/telecalling?user_id=${userId}`;
      }

      console.log("🌐 Requesting:", url);
      const response = await axios.get(url);

      if (response?.data?.success && response?.data?.lead) {
        console.log("✅ Lead received:", response.data.lead);
        setLead(response.data.lead);
      } else {
        console.warn("⚠️ No lead found or error:", response?.data?.message);
        setLead(null);
        setError(response?.data?.message || "No lead found");
      }
    } catch (err) {
      console.error("💥 Lead fetch error:", err);
      setError("Failed to fetch lead");
    } finally {
      setLoading(false);
      console.log("⏹️ Lead fetch complete");
    }
  }, [dataType, searchTeleId]);

  useEffect(() => {
    if (dataType !== "search") {
      fetchLead();
    }
  }, [fetchLead, dataType]);

  const handleSubmitOutcome = async (form) => {
    const userId = getUserId();
    if (!userId) {
      console.warn("⚠️ Missing userId during outcome submission");
      setSnackbar({
        open: true,
        severity: "error",
        message: "Session expired or user ID missing",
      });
      return;
    }

    if (lead?.name === "Callback Queue") {
      console.log("ℹ️ Skipping submission for callback queue");
      setSnackbar({
        open: true,
        severity: "info",
        message: "No submission required for callback queue",
      });
      return;
    }

    const payload = {
      mobileNumber: lead.mobile_number,
      callStatus: form.callStatus,
      disposition: form.callStatus === "Connected" ? form.disposition : null,
      remarks: form.remarks,
      callback_time: form.disposition === "Call Back" ? form.callbackTime : null,
      loan_type: form.disposition === "Interested" ? form.loanType : null,
      employment: form.disposition === "Interested" ? form.employmentType : null,
      req_loan_amt: form.disposition === "Interested" ? form.loanAmount : null,
      monthly_income: form.disposition === "Interested" ? form.monthlyIncome : null,
      userId,
    };

    console.log("📤 Submitting outcome:", payload);

    try {
      setSubmitting(true);
      const res = await fetch("https://be.corebankers.in/call-outcome", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error("Submission failed");

      console.log("✅ Outcome submitted successfully");
      setSnackbar({
        open: true,
        severity: "success",
        message: "Outcome saved. Loading next lead...",
      });

      await fetchLead();
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      console.error("💥 Error submitting outcome:", e);
      setSnackbar({
        open: true,
        severity: "error",
        message: "Failed to save outcome",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box className="pageWrapper">
      <TopBar
        onRefresh={fetchLead}
        dataType={dataType}
        onDataTypeChange={setDataType}
      />

      <Box className="contentWrapper">
        {dataType === "search" && (
          <Paper
            elevation={3}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              p: 2,
              mb: 3,
              borderRadius: 2,
              backgroundColor: "#f9f9f9",
            }}
          >
            <Typography variant="subtitle1" fontWeight={600} sx={{ minWidth: 140 }}>
              🔍 Search Tele ID
            </Typography>

            <TextField
              fullWidth
              placeholder="Enter Tele ID"
              variant="outlined"
              size="small"
              value={searchTeleId}
              onChange={(e) => setSearchTeleId(e.target.value)}
              sx={{
                flexGrow: 1,
                input: { fontWeight: 500 },
              }}
            />

            <Button
              variant="contained"
              onClick={fetchLead}
              sx={{
                px: 3,
                height: 40,
                fontWeight: 600,
                textTransform: "none",
              }}
            >
              Search
            </Button>
          </Paper>
        )}

        {loading ? (
          <Box className="loadingBox">
            <CircularProgress />
          </Box>
        ) : error ? (
          <Paper elevation={0} className="errorCard">
            <Typography variant="h6" fontWeight="700" gutterBottom>
              {error}
            </Typography>
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={fetchLead}
            >
              Try Again
            </Button>
          </Paper>
        ) : (
          <>
            <LeadCard lead={lead} onOpenHistory={() => setHistoryOpen(true)} />
            <Box className="outcomeCard">
              <Paper elevation={3} className="outcomeCard">
                <CallOutcomeForm
                  key={lead?.mobile_number}
                  onSubmit={handleSubmitOutcome}
                  submitting={submitting}
                  adminId={getUserId()}
                />
              </Paper>
            </Box>
          </>
        )}
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}